/*
 * camera-reader/reader.js — orchestrator for the accessible camera OCR reader.
 *
 * Inputs: live camera (via CRFraming), file upload (shared <x-tool-uploader>),
 * clipboard paste (Ctrl/Cmd-V). Pipeline for every source:
 *
 *   source → (EXIF/orientation upright) → OcrPre.process(auto) (Tesseract lane
 *   only) → the shared OCR door (public/js/ocr/ocr-engine.js: PP-OCRv5 for the
 *   scripts it knows, Tesseract otherwise)
 *   (one reused worker, LSTM, PSM 3) → real script-based language auto-detect
 *   (one re-run with the right model when the detected script mismatches) →
 *   conditional 90/180/270 orientation rescue on a poor first pass → result.
 *
 * The recognised text goes to CRRead (read-aloud). auto-read speaks it at once.
 *
 * Fixes the prototype's root defects: null uploadPreview crash, hardcoded/unmapped
 * Tesseract worker+core paths (now RU-proxy-aware via window.__CR paths), no
 * preprocessing, and fake "auto" language (page-locale only).
 *
 * Public: window.CameraReader = { init }
 */
(function () {
  'use strict';

  var CR = window.__CR || {};
  var i18n = CR.i18n || {};
  function t(k, d) { return (i18n && i18n[k]) || d || k; }
  var $ = function (id) { return document.getElementById(id); };

  // locale → default OCR model; model → BCP-47 speech lang
  var LOCALE_TO_TESS = { en: 'eng', ru: 'rus', ko: 'kor', ar: 'ara', id: 'ind', pt: 'por', de: 'deu', es: 'spa', fr: 'fra', uk: 'ukr', tr: 'tur' };
  var TESS_TO_SPEECH = {
    eng: 'en-US', rus: 'ru-RU', kor: 'ko-KR', ara: 'ar-SA', ind: 'id-ID', por: 'pt-BR', deu: 'de-DE', spa: 'es-ES', fra: 'fr-FR',
    chi_sim: 'zh-CN', chi_tra: 'zh-TW', jpn: 'ja-JP', ita: 'it-IT', tur: 'tr-TR', hin: 'hi-IN', tha: 'th-TH', vie: 'vi-VN', pol: 'pl-PL', ukr: 'uk-UA', nld: 'nl-NL'
  };
  var TESS_NAMES = {
    eng: 'English', rus: 'Русский', kor: '한국어', ara: 'العربية', ind: 'Bahasa Indonesia', por: 'Português', spa: 'Español', fra: 'Français', deu: 'Deutsch',
    chi_sim: '中文', chi_tra: '中文 (繁體)', jpn: '日本語', ita: 'Italiano', tur: 'Türkçe', hin: 'हिन्दी', tha: 'ไทย', vie: 'Tiếng Việt', pol: 'Polski', ukr: 'Українська', nld: 'Nederlands'
  };

  var locale = CR.locale || (document.documentElement.lang || 'en');
  var els = {};
  var worker = null, workerLang = null, busy = false;
  var lastCanvas = null;        // last captured/loaded canvas (for rotate re-OCR)
  var rotateDeg = 0;

  // ── state machine ─────────────────────────────────────────────────────────
  // intake → camera → busy → result. One attribute on the app root drives every
  // bit of visibility (the [data-when] rules in the blade), which is why a state
  // cannot half-render. The old page carried two independent switches — a tab
  // `active` class and a per-block `hidden` — and they could disagree: leaving
  // the camera tab hid the panel while the stream kept running behind it.
  var state = 'intake';
  function setState(next) {
    if (state === next) return;
    // Cross-fade the swap where the browser can. Through the primitive, never
    // `document.startViewTransition` directly: a transition has three promises
    // and two of them REJECT when it is skipped (tab hidden, another
    // transition pre-empting it), which is how /puzzle-maker produced an
    // unhandled rejection on every such click. The primitive also honours
    // prefers-reduced-motion and simply runs the function where the API is
    // missing, so this needs no fallback of its own.
    // Guard: tests/Unit/ViewTransitionGoesThroughThePrimitiveTest.php
    //
    // ⚠️ `state` is assigned SYNCHRONOUSLY, before the transition. The callback
    // runs on a later frame, and showProgress() and the space-bar shortcut both
    // read `state` right after calling this — deferring the assignment would
    // have them decide on the previous state, which is a race that only shows
    // up on a fast machine with a fast OCR pass.
    state = next;
    var paint = function () {
      var app = $('crApp');
      if (app) app.setAttribute('data-state', next);
    };
    if (window.__viewTransition) window.__viewTransition(paint); else paint();
    // Leaving the reading state always silences speech: a voice reading text
    // the person can no longer see is the phantom-speech complaint in another
    // costume.
    if (next !== 'result' && window.CRRead) { try { window.CRRead.stop(); } catch (e) {} }
    if (next !== 'result') closeMore();
  }

  // ── captured pages ────────────────────────────────────────────────────────
  // Camera sessions average 5.07 results each (60-day measurement, 1034 results
  // over 204 sessions) against 1.28 for uploads — people already photograph
  // several pages in a row. Until now every shot REPLACED the previous text, so
  // a two-page letter could only be read in two halves. Pages accumulate on
  // request and the chip says how many there are.
  var pages = [];
  var appendNext = false;

  // ── Unicode script detection → best Tesseract model ──
  function detectScriptLang(text) {
    var c = { cyr: 0, han: 0, kana: 0, hang: 0, arab: 0, thai: 0, deva: 0, latin: 0 };
    for (var i = 0; i < text.length; i++) {
      var ch = text.charCodeAt(i);
      if (ch >= 0x0400 && ch <= 0x04FF) c.cyr++;
      else if (ch >= 0x3040 && ch <= 0x30FF) c.kana++;
      else if (ch >= 0xAC00 && ch <= 0xD7A3) c.hang++;
      else if (ch >= 0x4E00 && ch <= 0x9FFF) c.han++;
      else if (ch >= 0x0600 && ch <= 0x06FF) c.arab++;
      else if (ch >= 0x0E00 && ch <= 0x0E7F) c.thai++;
      else if (ch >= 0x0900 && ch <= 0x097F) c.deva++;
      else if ((ch >= 0x41 && ch <= 0x5A) || (ch >= 0x61 && ch <= 0x7A) || (ch >= 0xC0 && ch <= 0x24F)) c.latin++;
    }
    var order = ['cyr', 'kana', 'hang', 'han', 'arab', 'thai', 'deva', 'latin'];
    var best = null, max = 0;
    order.forEach(function (k) { if (c[k] > max) { max = c[k]; best = k; } });
    if (!best || max < 3) return null;
    switch (best) {
      case 'cyr': return 'rus';
      case 'kana': return 'jpn';
      case 'hang': return 'kor';
      case 'han': return c.kana > 0 ? 'jpn' : 'chi_sim';
      case 'arab': return 'ara';
      case 'thai': return 'tha';
      case 'deva': return 'hin';
      case 'latin': return LOCALE_TO_TESS[locale] && isLatin(LOCALE_TO_TESS[locale]) ? LOCALE_TO_TESS[locale] : 'eng';
    }
    return null;
  }
  function isLatin(tess) { return ['eng', 'ind', 'por', 'spa', 'fra', 'deu', 'ita', 'tur', 'vie', 'pol', 'nld'].indexOf(tess) >= 0; }

  // "auto" recognises the page-locale language + English (the overwhelmingly
  // common "my language or English" case) in one bilingual pass. A single wrong-
  // script model can't emit the right script, so a post-hoc re-run can't rescue
  // it — a bilingual pass is what actually works. Other scripts: pick manually.
  function autoLangs() {
    var base = LOCALE_TO_TESS[locale] || 'eng';
    return base === 'eng' ? 'eng' : base + '+eng';
  }
  function primaryOf(langStr) { return String(langStr).split('+')[0]; }

  // ── OCR engine (one, reused; recreated only on language change) ──
  // Обёртка tesseract после terminate() мертва: внутри у неё Worker = null, и
  // следующий recognize() падает на `null.postMessage` (TIMBRICA-FRONTEND-J1,
  // Android/Yandex, 07.09.2026). Поэтому закрытие — только через dropWorker(),
  // который в тот же момент забывает движок; и есть ровно один способ узнать
  // такую смерть по ошибке — isDeadWorkerError.
  function dropWorker() {
    var w = worker; worker = null; workerLang = null;
    if (!w) return;
    try { var p = w.close(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
  }
  function isDeadWorkerError(err) {
    var m = String(err && err.message || err || '');
    return /reading 'postMessage'|null.*postMessage|postMessage.*null|terminated/i.test(m);
  }
  async function ensureWorker(lang) {
    if (worker && workerLang === lang) return worker;
    if (worker) dropWorker();
    if (!window.OcrEngine) throw new Error('OcrEngine missing');
    worker = await window.OcrEngine.open(lang, {
      slug: 'camera-reader',
      tesseractUrl: CR.tesseractUrl, tesseractFallbackUrl: CR.tesseractFallbackUrl,
      tesseract: { langPath: CR.langPath, workerPath: CR.workerPath, corePath: CR.corePath },
      onProgress: function () { setProgress(null, t('downloading_lang', 'Downloading language data…')); },
    });
    workerLang = lang;
    return worker;
  }
  var RECOGNIZE = { onLine: function (l) { if (l && l.total) setProgress(Math.round((l.index / l.total) * 100), t('processing', 'Recognizing text')); } };
  // The neural lane wants the photo as it is (Sauvola binarisation costs it
  // accuracy, measured 12.09.2026); OcrPre stays for the Tesseract lane.
  function prepared(w, canvas) { return w.kind === 'neural' ? canvas : preprocess(canvas); }

  function canvasToUpright(canvas, deg) {
    if (!deg) return canvas;
    var rad = deg * Math.PI / 180, s = Math.abs(Math.sin(rad)), co = Math.abs(Math.cos(rad));
    var w = canvas.width, h = canvas.height;
    var nw = Math.round(w * co + h * s), nh = Math.round(w * s + h * co);
    var out = document.createElement('canvas'); out.width = nw; out.height = nh;
    var ctx = out.getContext('2d');
    ctx.translate(nw / 2, nh / 2); ctx.rotate(rad); ctx.drawImage(canvas, -w / 2, -h / 2);
    return out;
  }

  function preprocess(canvas) {
    if (!window.OcrPre) return canvas;
    try {
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      var id = ctx.getImageData(0, 0, canvas.width, canvas.height);
      var out = window.OcrPre.process(id, { auto: true });
      var pc = document.createElement('canvas'); pc.width = out.width; pc.height = out.height;
      pc.getContext('2d').putImageData(out, 0, 0);
      return pc;
    } catch (e) { return canvas; }
  }

  function scaleCanvas(canvas, maxDim) {
    var w = canvas.width, h = canvas.height, m = Math.max(w, h);
    if (m <= maxDim) return canvas;
    var k = maxDim / m;
    var out = document.createElement('canvas'); out.width = Math.round(w * k); out.height = Math.round(h * k);
    out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
    return out;
  }

  // conditional orientation rescue: recognise a downscale at 90/180/270, keep the best
  async function orientationRescue(w, uprightCanvas) {
    var small = scaleCanvas(uprightCanvas, 800);
    var best = null;
    for (var _i = 0, degs = [90, 180, 270]; _i < degs.length; _i++) {
      var deg = degs[_i];
      var rc = canvasToUpright(small, deg);
      try {
        var r = await w.recognize(rc);
        var score = ((r.words && r.words.length) || (r.text || '').split(/\s+/).filter(Boolean).length) * (r.confidence || 0);
        if (!best || score > best.score) best = { deg: deg, score: score, conf: r.confidence || 0 };
      } catch (e) {}
    }
    return best;
  }

  // ── the pipeline ──
  async function runOCR(sourceCanvas, meta) {
    if (busy) return;
    meta = meta || {};
    busy = true;
    lastCanvas = sourceCanvas;
    rotateDeg = 0;
    showProgress(true);
    setProgress(null, t('loading_ocr', 'Loading OCR engine…'));
    // ⛔ Do not touch els.result.hidden here. Visibility belongs to the state
    // machine, and `[hidden]` carries !important, so a leftover attribute beats
    // [data-when] and the result panel stays invisible while its text is set —
    // a failure a test that reads .value cannot see, only one that measures a
    // box can (which is why the guard below asserts geometry).

    var picked = els.lang ? els.lang.value : 'auto';
    var isAuto = picked === 'auto';
    var lang = isAuto ? autoLangs() : picked;

    try {
      var w = await ensureWorker(lang);
      var src = prepared(w, sourceCanvas);
      var data = await w.recognize(src, RECOGNIZE);
      var text = (data.text || '').trim();
      var conf = Math.round(data.confidence || 0);
      var wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0;

      // orientation rescue on a clearly poor first pass (sideways phone shot)
      if ((conf < 55 || wordCount < 3) && (meta.fromCamera || meta.fromUpload)) {
        setProgress(null, t('checking_orientation', 'Checking orientation…'));
        var rescue = await orientationRescue(w, sourceCanvas);
        if (rescue && rescue.conf > conf + 8) {
          rotateDeg = rescue.deg;
          var up = canvasToUpright(sourceCanvas, rescue.deg);
          src = prepared(w, up);
          data = await w.recognize(src, RECOGNIZE);
          text = (data.text || '').trim();
          conf = Math.round(data.confidence || 0);
          wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0;
          lastCanvas = up;
        }
      }

      // language label: from the dominant script of the recognised text (a
      // bilingual auto pass emits the real script, so this reliably tells rus
      // from eng); for a manual pick, it's just the picked model.
      var detected = isAuto ? (detectScriptLang(text) || primaryOf(lang)) : primaryOf(lang);

      showProgress(false);
      if (!text) {
        // The remedy for "no text found" is to rotate and try again, and the
        // rotate controls live in the result view — so a failed read must land
        // somewhere the remedy is reachable, not bounce back to the doors with
        // a toast that is gone in four seconds.
        showNoText();
        trackError('no_text', 'empty, conf=' + conf);
        busy = false;
        return;
      }

      showResult(text, conf, detected, isAuto);
      trackComplete({ lang: detected, confidence: conf, chars: text.length, auto: isAuto ? 1 : 0, src: meta.fromCamera ? 'camera' : (meta.fromPaste ? 'paste' : 'upload'), rot: rotateDeg, engine: w.kind });
    } catch (err) {
      if (!meta._retried && isDeadWorkerError(err)) {
        // воркер умер под нами (pagehide/bfcache, смена языка на лету) —
        // это не ошибка человека: пересоздаём и повторяем один раз
        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'ocr_worker_dead_retry' }); } catch (e2) {}
        dropWorker();
        busy = false;
        meta._retried = true;
        return runOCR(sourceCanvas, meta);
      }
      showProgress(false);
      if (window.__handleError) window.__handleError(err, 'camera-reader');
      if (window.__toast) window.__toast(String(err && err.message || err).slice(0, 150), 'error');
      trackError('ocr', err && err.message);
    }
    busy = false;
  }

  // A read that found nothing is still a state the person is standing in: say
  // so where they are looking, keep the rotate-and-retry controls to hand, and
  // leave no stale text or speech behind.
  function showNoText() {
    var msg = t('no_text', 'No text found. Try a clearer image or a different angle.');
    if (window.__toast) window.__toast(msg, 'warning');
    if (window.__announce) window.__announce(msg);
    appendNext = false;

    // A failed page must never cost the pages that succeeded. Someone three
    // pages into a letter whose fourth shot came out blurred keeps the three.
    if (pages.length) {
      if (els.rotateRow) els.rotateRow.hidden = !lastCanvas;
      setState('result');
      return;
    }

    // The message goes in its own note, not into the text views — those belong
    // to read-aloud.js, which swaps them while speaking; writing into them here
    // would fight it and the message would vanish on the next play.
    if (els.notice) { els.notice.textContent = msg; els.notice.hidden = false; }
    if (els.text) els.text.value = '';
    if (window.CRRead) window.CRRead.clear();
    if (els.conf) { els.conf.textContent = ''; els.conf.className = 'cr-chip'; }
    if (els.pageChip) els.pageChip.hidden = true;
    if (els.detLang) els.detLang.hidden = true;
    if (els.rotateRow) els.rotateRow.hidden = !lastCanvas;
    if (els.morePanel) els.morePanel.hidden = false;         // the remedy, already open
    if (els.more) els.more.setAttribute('aria-expanded', 'true');
    setState('result');
  }

  // ── result UI ──
  function showResult(text, conf, detectedLang, wasAuto) {
    // A page is added only when the person asked for it; otherwise a new shot
    // is a new text, which is what a single-page reader expects.
    if (appendNext && pages.length) pages.push(text); else pages = [text];
    appendNext = false;
    var full = pages.join('\n\n');

    if (els.text) els.text.value = full;
    if (els.notice) els.notice.hidden = true;
    if (els.rotateRow) els.rotateRow.hidden = false;
    setState('result');

    if (els.pageChip) {
      if (pages.length > 1) {
        els.pageChip.hidden = false;
        els.pageChip.textContent = t('pages_count', 'Pages: :n').replace(':n', pages.length);
      } else els.pageChip.hidden = true;
    }

    // confidence chip
    if (els.conf) {
      els.conf.textContent = t('confidence', 'Confidence') + ': ' + conf + '%';
      els.conf.className = 'cr-chip ' + (conf < 55 ? 'cr-chip-low' : conf < 80 ? 'cr-chip-mid' : 'cr-chip-ok');
    }
    if (conf < 55 && window.__toast) window.__toast(t('low_confidence', 'Low confidence — try better lighting, a flatter angle, or move closer.'), 'warning');

    // detected-language chip (only when auto picked something)
    if (els.detLang) {
      if (wasAuto) { els.detLang.hidden = false; els.detLang.textContent = t('detected', 'Detected') + ': ' + (TESS_NAMES[detectedLang] || detectedLang); }
      else els.detLang.hidden = true;
    }

    var speechLang = TESS_TO_SPEECH[detectedLang] || (detectedLang.slice(0, 2));
    var langPrefix = speechLang.split('-')[0];

    // hand to read-aloud — the WHOLE text, so a multi-page capture is read as
    // one document rather than as the last page only.
    if (window.CRRead) window.CRRead.setText(full, langPrefix, speechLang);

    // SR announce + optional auto-read
    if (window.__announce) window.__announce(t('recognized_text', 'Recognized text') + '. ' + text.slice(0, 160));
    if (els.autoRead && els.autoRead.checked && window.CRRead) {
      // give voices a tick to be ready
      setTimeout(function () { window.CRRead.play(); }, 250);
    }
  }

  // ── progress ──
  // `busy` owns the stage while work runs, so the viewfinder and the previous
  // text step aside instead of stacking a fourth block onto the column.
  var stateBeforeBusy = 'intake';
  function showProgress(on) {
    if (on) { if (state !== 'busy') stateBeforeBusy = state; setState('busy'); }
    else if (state === 'busy') setState(pages.length ? 'result' : stateBeforeBusy);
  }
  function setProgress(pct, label) {
    if (els.progFill) els.progFill.style.width = (pct == null ? 15 : pct) + '%';
    if (els.progText && label) els.progText.textContent = label + (pct != null ? ' ' + pct + '%' : '');
    if (els.progress && pct != null) els.progress.setAttribute('aria-valuenow', pct);
  }

  // ── image loading (EXIF-upright) ──
  async function loadImageFile(file, meta) {
    try {
      await loadImageFileCore(file, meta);
    } catch (e) {
      // Chrome on Android has no HEIC decoder; a corrupt JPEG fails the same way.
      // Both used to surface as an unhandled «image load failed» (Sentry M6)
      // while the page stayed silent. Say it, and count the format.
      var ext = String((file && file.name) || '').split('.').pop().toLowerCase();
      trackFeature('image_unreadable', { ext: ext, type: (file && file.type) || '', heic: (window.HeicDecode && HeicDecode.isHeic(file)) ? 1 : 0 });
      if (window.__toast) window.__toast(t('image_unreadable', 'This image could not be read. Try a JPG or PNG.'), 'error');
    }
  }
  async function loadImageFileCore(file, meta) {
    // iPhone photos arrive as HEIC on a phone that cannot draw them — the shared
    // decoder turns them into a JPEG first (same door as the other photo tools).
    if (window.HeicDecode && typeof HeicDecode.normalize === 'function' && HeicDecode.isHeic(file)) {
      file = await HeicDecode.normalize(file);
    }
    var canvas;
    try {
      if (window.createImageBitmap) {
        var bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
        canvas = document.createElement('canvas'); canvas.width = bmp.width; canvas.height = bmp.height;
        canvas.getContext('2d').drawImage(bmp, 0, 0);
        try { bmp.close(); } catch (e) {}
      }
    } catch (e) { canvas = null; }
    if (!canvas) {
      canvas = await new Promise(function (res, rej) {
        var url = URL.createObjectURL(file), img = new Image();
        img.onload = function () { var c = document.createElement('canvas'); c.width = img.naturalWidth; c.height = img.naturalHeight; c.getContext('2d').drawImage(img, 0, 0); URL.revokeObjectURL(url); res(c); };
        img.onerror = function () { URL.revokeObjectURL(url); rej(new Error('image load failed')); };
        img.src = url;
      });
    }
    // Switch to the upload/result view, stopping the camera if it was running.
    // stopCamera(), not CRFraming.stop(): the latter kills the stream but
    // leaves reader.js still believing the camera is open, so the next press on
    // the camera door would "close" an already-closed camera and do nothing.
    if (camOpen || (window.CRFraming && window.CRFraming.isOpen())) stopCamera();
    runOCR(canvas, meta || { fromUpload: true });
  }

  // ── manual rotate + re-OCR ──
  function rotateAndRerun(deg) {
    if (!lastCanvas) return;
    var rc = canvasToUpright(lastCanvas, deg);
    runOCR(rc, { fromUpload: true });
  }

  // ── secondary actions ──
  function closeMore() {
    if (els.morePanel) els.morePanel.hidden = true;
    if (els.more) els.more.setAttribute('aria-expanded', 'false');
  }

  // ── camera controls ──
  var camOpen = false;
  function syncCamBtn(on) {
    camOpen = on;
    if (els.switchCam) els.switchCam.hidden = !on;
    if (els.torch && !on) els.torch.style.display = 'none';
    if (els.zoomWrap && !on) els.zoomWrap.style.display = 'none';
    if (on) setState('camera');
    else if (state === 'camera') setState(pages.length ? 'result' : 'intake');
  }

  function stopCamera() {
    if (window.CRFraming && window.CRFraming.isOpen()) window.CRFraming.stop();
    syncCamBtn(false);
  }

  async function openCamera() {
    if (!window.CRFraming) return;
    if (camOpen) { stopCamera(); return; }
    // Until 17.09.2026 this lane emitted NO telemetry whatsoever — framing.js
    // tracked nothing and reader.js only counted copy/download/paste. The
    // camera is 78 % of all results (1034 of 1326 over 60 days) and we could
    // not see a single thing about it: not how often it opens, not how often a
    // permission is refused, not whether a shot was the person's or the
    // auto-capture's. Counting the open and its verdict is the cheapest part.
    trackFeature('camera_open');
    var t0 = Date.now();
    try {
      await window.CRFraming.start({
        i18n: i18n,
        els: { video: els.video, guide: els.guide, torch: els.torch, zoom: els.zoom, zoomWrap: els.zoomWrap, hint: els.camHint },
        autoCapture: els.autoCapture ? els.autoCapture.checked : true,
        onCapture: function (canvas, info) {
          trackFeature((info && info.auto) ? 'capture_auto' : 'capture_manual');
          runOCR(canvas, { fromCamera: true, auto: info && info.auto });
        }
      });
      syncCamBtn(true);
      trackFeature('camera_ready');
      try { window.__ae && window.__ae('feature_use', { slug: 'camera-reader', f: 'camera_open_ms', ms: Date.now() - t0 }); } catch (e) {}
    } catch (e) {
      // Name the CAUSE, not just "camera failed": denied, busy and absent are
      // three different things to do next about, and one message for all three
      // is what sends people to fix permissions on a machine that has no camera
      // — exactly what comment #1689 described.
      var d = (window.CRFraming && window.CRFraming.describeError)
        ? window.CRFraming.describeError(e)
        : { code: 'generic', message: null };
      if (window.__toast) window.__toast(d.message || t('camera_error', 'Cannot access the camera.'), 'error');
      trackError('camera_' + d.code, e && (e.message || e.name || e));
      syncCamBtn(false);
    }
  }

  // ── copy / clear / download ──
  function bindResultActions() {
    if (els.copy) els.copy.addEventListener('click', function () {
      var v = els.text ? els.text.value : '';
      var done = function () { var l = els.copy.querySelector('[data-label]'); if (l) { var o = l.textContent; l.textContent = t('copied', 'Copied!'); setTimeout(function () { l.textContent = o; }, 1600); } };
      if (navigator.clipboard) navigator.clipboard.writeText(v).then(done, function () { els.text.select(); document.execCommand('copy'); done(); });
      else { els.text.select(); document.execCommand('copy'); done(); }
      trackFeature('copy');
    });
    if (els.download) els.download.addEventListener('click', function () {
      var v = els.text ? els.text.value : ''; if (!v) return;
      var blob = new Blob([v], { type: 'text/plain;charset=utf-8' });
      if (window.__downloadFile) window.__downloadFile(blob, 'camera-reader.txt');
      trackFeature('download');
    });
    // "Clear" is gone as a separate control: "New text" in the secondary bar
    // does the same thing AND returns to the two doors, which is what the
    // person wanted next anyway. One verb, not two.
    if (els.text) els.text.addEventListener('input', debounce(function () {
      // an edit rewrites the page set: the text on screen is now the document
      pages = [els.text.value];
      // re-chunk read-aloud from the edited text (uses whatever lang was set)
      if (window.CRRead) window.CRRead.setText(els.text.value, (TESS_TO_SPEECH[workerLang] || 'en').split('-')[0], TESS_TO_SPEECH[workerLang] || 'en-US');
    }, 400));
    document.querySelectorAll('[data-rotate]').forEach(function (b) {
      b.addEventListener('click', function () { rotateAndRerun(parseInt(this.dataset.rotate, 10) || 90); });
    });
  }

  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }

  // ── telemetry ──
  function trackFeature(f) { try { window.__ae && window.__ae('feature_use', { slug: 'camera-reader', f: f }); } catch (e) {} }
  function trackError(type, msg) { try { window.__aeToolError && window.__aeToolError({ error_type: type, msg: String(msg || '').slice(0, 400) }); } catch (e) {} }
  function trackComplete(p) { try { window.__aeToolComplete && window.__aeToolComplete(p); } catch (e) {} }

  // ── init ──
  function init() {
    els = {
      app: $('crApp'),
      lang: $('crLang'), autoRead: $('crAutoRead'), autoCapture: $('crAutoCapture'),
      video: $('crVideo'), guide: $('crGuide'), camStage: $('crCamStage'), camHint: $('crCamHint'),
      camStart: $('crCamStart'), camStop: $('crCamStop'), capture: $('crCapture'), switchCam: $('crSwitch'),
      torch: $('crTorch'), zoom: $('crZoom'), zoomWrap: $('crZoomWrap'), mute: $('crMute'),
      progress: $('crProgress'), progFill: $('crProgressFill'), progText: $('crProgressText'),
      result: $('crResult'), text: $('crText'), reading: $('crReading'),
      conf: $('crConfidence'), detLang: $('crDetectedLang'), pageChip: $('crPageChip'),
      notice: $('crNotice'),
      copy: $('crCopy'), download: $('crDownload'), rotateRow: $('crRotateRow'),
      more: $('crMore'), morePanel: $('crMorePanel'),
      addPage: $('crAddPage'), again: $('crAgain'),
      easyFont: $('crEasyFont'), looseLines: $('crLooseLines')
    };

    bindResultActions();

    if (els.camStart) els.camStart.addEventListener('click', openCamera);
    if (els.camStop) els.camStop.addEventListener('click', stopCamera);
    if (els.capture) els.capture.addEventListener('click', function () { if (window.CRFraming) window.CRFraming.captureNow(); });
    if (els.switchCam) els.switchCam.addEventListener('click', function () { trackFeature('switch_camera'); if (window.CRFraming) window.CRFraming.switchCamera(); });
    if (els.autoCapture) els.autoCapture.addEventListener('change', function () { if (window.CRFraming) window.CRFraming.setAutoCapture(this.checked); });
    if (els.torch) els.torch.addEventListener('click', function () { trackFeature('torch'); });
    if (els.mute) els.mute.addEventListener('click', function () {
      var on = this.getAttribute('aria-pressed') === 'true';
      this.setAttribute('aria-pressed', (!on).toString());
      if (window.CRFraming) window.CRFraming.setMuted(!on);
    });

    // secondary actions live one tap away so the bar stays at four buttons
    if (els.more) els.more.addEventListener('click', function () {
      var open = els.morePanel && els.morePanel.hidden;
      if (els.morePanel) els.morePanel.hidden = !open;
      this.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    // "Add a page" arms the next capture to append; "New text" drops everything
    // and returns to the two doors.
    if (els.addPage) els.addPage.addEventListener('click', function () {
      appendNext = true;
      closeMore();
      trackFeature('add_page');
      if (window.__toast) window.__toast(t('page_added', 'Capture the next page'), 'info');
      // Back to the two doors, not straight into the camera: the next page can
      // just as well be a file, and someone who arrived by file has no reason
      // to be handed a viewfinder (or a camera-permission prompt on a desktop
      // that has no camera).
      if (camOpen) setState('camera'); else setState('intake');
    });
    if (els.again) els.again.addEventListener('click', function () {
      pages = []; appendNext = false; lastCanvas = null;
      if (els.text) els.text.value = '';
      if (els.notice) els.notice.hidden = true;
      if (window.CRRead) window.CRRead.clear();
      closeMore();
      setState('intake');
      trackFeature('new_text');
    });

    // reading comfort (the accessibility gap this tool shipped with)
    var applyComfort = function () {
      var easy = els.easyFont && els.easyFont.checked;
      var loose = els.looseLines && els.looseLines.checked;
      [els.reading, els.text].forEach(function (n) {
        if (!n) return;
        n.classList.toggle('cr-easy', !!easy);
        n.classList.toggle('cr-loose', !!loose);
      });
    };
    [els.easyFont, els.looseLines].forEach(function (n) {
      if (!n) return;
      n.addEventListener('change', function () { applyComfort(); trackFeature(n === els.easyFont ? 'easy_font' : 'loose_lines'); });
      if (window.__toolSettings) window.__toolSettings.bind('camera-reader', n, n.id, false);
    });
    applyComfort();

    // upload (shared uploader)
    var up = $('crUpload');
    if (up) up.addEventListener('tool-uploader:file', function (e) {
      var f = e.detail && e.detail.file;
      if (f && /^image\//.test(f.type)) loadImageFile(f, { fromUpload: true });
    });

    // clipboard paste
    document.addEventListener('paste', function (e) {
      var items = (e.clipboardData && e.clipboardData.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].type && items[i].type.indexOf('image') === 0) {
          var f = items[i].getAsFile();
          if (f) { e.preventDefault(); loadImageFile(f, { fromPaste: true }); trackFeature('paste'); return; }
        }
      }
    });

    // language: default from page locale; persist choice
    if (els.lang) {
      if (window.__toolSettings) window.__toolSettings.bind('camera-reader', els.lang, 'lang', 'auto');
      else if (LOCALE_TO_TESS[locale]) els.lang.value = 'auto';
      // re-run OCR if a language is chosen after a result exists
      els.lang.addEventListener('change', function () { if (lastCanvas && !busy) runOCR(lastCanvas, { fromUpload: true }); });
    }
    if (els.autoRead && window.__toolSettings) window.__toolSettings.bind('camera-reader', els.autoRead, 'autoRead', true);
    if (els.autoCapture && window.__toolSettings) window.__toolSettings.bind('camera-reader', els.autoCapture, 'autoCapture', true);

    // Preview row: only meaningful for a server HD voice (the device voices are
    // the ones the person already hears everywhere else, and they have no sample
    // file). Shown by the voice list itself, so it can never advertise a preview
    // for a voice that has none.
    var prevRow = $('crVoicePreviewRow'), prevBtn = $('crVoicePreview'), prevAudio = null;
    var syncPreviewRow = function () {
        var v = $('crVoice');
        var isHd = !!(v && v.value && v.value.indexOf('hd:') === 0);
        var url = isHd && window.CRHd ? window.CRHd.previewUrl(v.value.slice(3)) : null;
        if (prevRow) prevRow.hidden = !url;
    };
    if (prevBtn) prevBtn.addEventListener('click', function () {
        var v = $('crVoice');
        if (!v || !window.CRHd) return;
        var url = window.CRHd.previewUrl(String(v.value).slice(3));
        if (!url) return;
        if (!prevAudio) prevAudio = new Audio();
        // Through the gate: this is a click handler, so activation is alive, but
        // the shared element keeps one unlock for the whole page.
        if (window.__audioGate && window.__audioGate.play) window.__audioGate.play(prevAudio, url).catch(function () {});
        else { prevAudio.src = url; prevAudio.play().catch(function () {}); }
        trackFeature('voice_preview');
    });
    if ($('crVoice')) $('crVoice').addEventListener('change', function () {
        syncPreviewRow();
        trackFeature(String(this.value).indexOf('hd:') === 0 ? 'voice_hd_picked' : 'voice_device_picked');
    });

    // read-aloud module
    if (window.CRRead) window.CRRead.init({
        onVoicesLoaded: syncPreviewRow,
      i18n: i18n,
      els: {
        reading: els.reading, textarea: els.text,
        play: $('crPlay'), stop: $('crStop'), voice: $('crVoice'),
        speed: $('crSpeed'), speedVal: $('crSpeedVal'),
        // The same note "no text found" uses: read-aloud writes the "preparing
        // the voice" line into it while the server synthesises, so the three
        // seconds before the first sound are visible as well as announced.
        notice: els.notice
      }
    });

    // text-size slider (independent of speech)
    var sizeR = $('crTextSize'), sizeV = $('crTextSizeVal');
    if (sizeR) {
      var applySize = function () {
        var px = sizeR.value + 'px';
        if (els.text) els.text.style.fontSize = px;
        if (els.reading) els.reading.style.fontSize = px;
        if (sizeV) sizeV.textContent = px;
      };
      sizeR.addEventListener('input', applySize);
      if (window.__toolSettings) window.__toolSettings.bind('camera-reader', sizeR, 'textSize', '18');
      applySize();
    }
    if (els.speed && els.speed.value && $('crSpeedVal')) $('crSpeedVal').textContent = parseFloat(els.speed.value).toFixed(1) + '×';

    // keyboard shortcuts (guarded against typing in the textarea/inputs)
    document.addEventListener('keydown', function (e) {
      var tag = (e.target && e.target.tagName || '').toLowerCase();
      if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (tag === 'textarea' || tag === 'input' || tag === 'select' || (e.target && e.target.isContentEditable))) return;
      // Space plays/pauses only where there is something to read. Guarding on
      // the STATE rather than on a node's `hidden` keeps this true after the
      // markup moves — the old guard dereferenced a panel that no longer exists.
      if (e.key === ' ' && state === 'result' && window.CRRead) { e.preventDefault(); window.CRRead.toggle(); }
    });

    // cleanup
    // pagehide, а не только beforeunload: на Android страница уходит в bfcache и
    // ВОЗВРАЩАЕТСЯ с тем же `worker`, чей Worker уже terminate()-нут. Обёртку
    // после terminate забываем в ту же секунду (dropWorker), а не держим.
    var teardown = function () {
      try { if (window.CRFraming) window.CRFraming.stop(); } catch (e) {}
      try { if (window.CRRead) window.CRRead.stop(); } catch (e) {}
      dropWorker();
    };
    window.addEventListener('pagehide', teardown);
    window.addEventListener('beforeunload', teardown);
  }

  window.CameraReader = { init: init };
})();
